import {Component, OnInit, OnDestroy, inject, Inject} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {Observable, Subject, Subscription} from 'rxjs';
import {TranslateService, TranslatePipe} from '@ngx-translate/core';
import {NgClass, DatePipe, AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {ProductChange} from '../../shared/model/product-change';
import {ProductChangeService} from '../../shared/service/product-change/product-change.service';
import {ErrorInfo} from '../../shared/model/error-info';
import {ErrorHandlingService} from '../../shared/service/error-handling/error-handling.service';
import {environment} from '../../../../environments/environment';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';
import {DeliveryType} from '../../shared/model/delivery-type';
import {DeliveryService} from '../../shared/service/delivery/delivery.service';
import {ObtainablePipe} from '../../shared/pipes/obtainable.pipe';
import {ArrayToStringPipe} from '../../shared/pipes/array-to-string.pipe';
import {YesNoPipe} from '../../shared/pipes/yes-no.pipe';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ErrorBoxComponent} from '../error-box/error-box.component';
import {DeliveryBasesComponent} from '../delivery-bases/delivery-bases.component';
import {MatCard} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatFabButton} from '@angular/material/button';
import {map} from 'rxjs/operators';
import {DacklBreakpoint, DacklBreakpointObserver} from '@datev/ngx-dackl';
import {HtmlViewerComponent} from '../html-viewer/html-viewer.component';


@Component({
    selector: 'myupdates-product-change-details',
    templateUrl: './product-change-details.component.html',
    styleUrls: ['./product-change-details.component.scss'],
  imports: [MatButton, MatIcon, RouterLink, MatCard, NgClass, DeliveryBasesComponent, ErrorBoxComponent,
    MatProgressSpinner, AsyncPipe, DatePipe, TranslatePipe, YesNoPipe, ArrayToStringPipe, ObtainablePipe, NgTemplateOutlet, HtmlViewerComponent, MatFabButton]
})
export class ProductChangeDetailsComponent implements OnInit, OnDestroy {
  productChange$?: Observable<ProductChange>;
  errorInfo$: Observable<ErrorInfo>;
  deliveryError$ = new Subject<boolean>();
  contentError$ = new Subject<boolean>();
  deliveryErrorOccurred = false;
  errorTitle = '';
  errorText = '';
  dateTranslation = '';
  dateTranslationMonth = '';
  translateErrorContentTitleKey = 'product-change-details.error_content_title';
  translateErrorContentKey = 'product-change-details.error_content';
  deliveryProductsRoute$?: Observable<string>;

  private adanSubscription?: Subscription;
  private errorContentTitleSubscription?: Subscription;
  private errorContentTextSubscription?: Subscription;
  protected readonly DeliveryType = DeliveryType;

  breakpoint$ = inject(DacklBreakpointObserver).breakpoint$;
  isSmallScreen$ = this.breakpoint$.pipe(map(breakpoint => breakpoint <= DacklBreakpoint.SM));

  constructor(private readonly route: ActivatedRoute,
              private readonly adobeTrackingService: AdobeTrackingService,
              private readonly productChangeService: ProductChangeService,
              private readonly deliveryService: DeliveryService,
              private readonly errorHandlingService: ErrorHandlingService,
              private readonly translate: TranslateService,
              @Inject('Window') private readonly window: Window) {
    this.errorInfo$ = this.errorHandlingService.errorStream$;
  }

  ngOnInit(): void {
    // Hinweis: ngOnInit wird nicht erneut aufgerufen, falls sich in der URL nur die Parameter ändern.
    // Wenn dies aber gefordert wird, kann das z.B. über ein subscribe auf die ActivatedRoute.paramMap gelöst werden
    const productChangeId = this.route.snapshot.paramMap.get('id') ?? 'unknown';
    this.productChange$ = this.errorHandlingService.bindErrorHandling(this.productChangeService.getProductChangeById(productChangeId));

    this.deliveryProductsRoute$ = this.productChange$.pipe(map(productChange => {
      if (productChange.deliveryType !== null && !!productChange.deliveryId) {
        return this.deliveryService.getDeliveryProductsRoute(productChange.deliveryType, productChange.deliveryId);
      }
      return '';
    }));

    this.adanSubscription = this.productChange$.subscribe(_productChange => {
      this.adobeTrackingService.publishClickEvent('Programmänderungen:Dokument:StandardDokument');
    });

    this.errorContentTitleSubscription = this.translate.get(this.translateErrorContentTitleKey).subscribe(res =>
      this.errorTitle = res);
    this.errorContentTextSubscription = this.translate.get(this.translateErrorContentKey).subscribe(res =>
      this.errorText = res);

    this.dateTranslation = this.translate.instant('common.date_format');
    this.dateTranslationMonth = this.translate.instant('common.date_format_month');
  }

  ngOnDestroy(): void {
    this.adanSubscription?.unsubscribe();
    this.errorContentTitleSubscription?.unsubscribe();
    this.errorContentTextSubscription?.unsubscribe();
  }

  isValidDeliveryInfos(productChange: ProductChange | null): boolean {

    this.deliveryErrorOccurred = false;

    if (!productChange) {
       this.deliveryErrorOccurred = true;
       return false;
    }

    const hasNoDeliveryBases = productChange.deliveryBases === null || productChange.deliveryBases.length === 0;
    const hasNoDeliveryWay = productChange.deliveredByRdt === null && productChange.deliveredByDownload === null;
    const hasNoDataTransformationAndNoBootRequired = productChange.dataTransformation === null && productChange.bootRequired == null;

    return !(productChange.onlineProduct ||
      (hasNoDeliveryBases && hasNoDeliveryWay && hasNoDataTransformationAndNoBootRequired));
  }

  shareByMail(productChange: ProductChange | null): void {
    if (!productChange) {
      return;
    }

    // Adobe Analytics Einbindung
    this.adobeTrackingService.publishClickEvent('Programmänderungen:Dokument:Teilen');

    const mailSubject = `Leseempfehlung \\ ${productChange.title} \\ DATEV MyUpdates`;
    const mailBody = `Ich möchte Ihnen den folgenden Beitrag in DATEV MyUpdates empfehlen:` +
      `%0A${productChange.title}%0A${environment.entryPoint}product-change/details/${productChange.id}%0A%0A` +
      `Mit freundlichem Gruß`;

    this.window.location.href = `mailto:?subject=${mailSubject}&body=${mailBody}`;
  }

}
